import type { FC } from 'react';
import { useLayoutEffect } from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import type { ReactShellApi } from 'src/components/layout/react-shell-type';
import { DownloadToast } from 'src/components/monolith-download';
import { useAxiosInstance } from 'src/hooks/use-axios';
import { useCognito } from 'src/hooks/use-cognito';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { baseUrl } from 'src/utils/base-url';
import { cn } from 'src/utils/cn';

/**
 * Our "dirty" helper to share React functionality in an easy way with Angular.
 */
export const ReactShell: FC = () => {
  const axios = useAxiosInstance();
  const cognito = useCognito();
  const isNewDesignActive = useIsNewDesignActive();

  useLayoutEffect(() => {
    // expose react shell api to angular
    const reactShell = {
      downloadFile(href: string) {
        const id = uuid();
        toast.success(<DownloadToast id={id} href={href} />, {
          id,
          duration: Infinity,
        });
      },
      axios,
      cognito,
    } as ReactShellApi;
    (window as any).reactShell = reactShell;

    // create angular iframe *after* react shell api is exposed
    const container = document.getElementById('angular-container');

    // this hook runs twice in React 18 dev mode.
    // if we find children, we already created the iframe.
    if (container?.children.length) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${baseUrl}angular#initialize`;
    iframe.name = 'embedded-angular';
    iframe.style.display = 'none';
    iframe.className = cn('print:left-0!');
    iframe.style.width = '100%';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.position = 'absolute';
    if (isNewDesignActive) iframe.style.zIndex = '1';
    container?.appendChild(iframe);
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  return null;
};
